import React, { useEffect, useState } from 'react';
import useScrollLock from '../../../hooks/useScrollLock';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAllProjects, createProject, updateProject, deleteProject } from '../../../store/slices/projectsSlice';
import { Project, CreateProjectDTO, UpdateProjectDTO } from './types';
import { FaTimes, FaTrash, FaEdit, FaSave } from 'react-icons/fa';

interface ProjectManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'list' | 'create';
  mode?: 'manage' | 'create';
  onProjectCreated?: (projectId: string) => void;
}

const ProjectManagementModal: React.FC<ProjectManagementModalProps> = ({
  isOpen,
  onClose,
  initialView = 'list',
  mode = 'manage',
  onProjectCreated,
}) => {
  const dispatch = useAppDispatch();
  const { projects, isLoading, error } = useAppSelector((state) => state.projects);

  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; projectId: string | null }>({
    isOpen: false,
    projectId: null,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateProjectDTO>({
    name: '',
    description: '',
    color: '#f97316', // Default orange color
  });
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAllProjects());
      dispatch(fetchAllProjects());
      // If mode is create, force show form. If manage, respect initialView
      setShowCreateForm(mode === 'create' || initialView === 'create');
    }
  }, [dispatch, isOpen, initialView, mode]);

  // Lock scroll when open
  useScrollLock(isOpen);

  const handleCreate = async () => {
    if (!formData.name.trim()) return;

    // Ensure color is always provided (required field)
    const projectData: CreateProjectDTO = {
      name: formData.name.trim(),
      description: formData.description?.trim() || undefined,
      color: formData.color || '#f97316', // Default to orange if not provided
    };

    try {
      const newProject = await dispatch(createProject(projectData)).unwrap();
      if (onProjectCreated) {
        onProjectCreated(newProject.id);
      }
      setFormData({ name: '', description: '', color: '#f97316' });
      setShowCreateForm(false);
      // If in create-only mode, close the modal after successful creation
      if (mode === 'create') {
        onClose();
      }
    } catch (err) {
      // Error is handled by Redux state
    }
  };

  const handleUpdate = async (id: string, data: UpdateProjectDTO) => {
    try {
      await dispatch(updateProject({ id, data })).unwrap();
      setEditingId(null);
    } catch (err) {
      // Error is handled by Redux state
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmation({
      isOpen: true,
      projectId: id,
    });
  };

  const confirmDelete = async () => {
    if (deleteConfirmation.projectId) {
      try {
        await dispatch(deleteProject(deleteConfirmation.projectId)).unwrap();
        setDeleteConfirmation({ isOpen: false, projectId: null });
      } catch (err) {
        // Error is handled by Redux state
      }
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, projectId: null });
  };

  const startEdit = (project: Project) => {
    setEditingId(project.id);
    setFormData({
      name: project.name,
      description: project.description || '',
      color: project.color || '#f97316',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', description: '', color: '#f97316' });
  };

  if (!isOpen) return null;

  const editingProject = editingId ? projects.find(p => p.id === editingId) : null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FFF8F0] rounded-2xl p-4 sm:p-10 max-w-2xl w-full border border-gray-200/60 shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-medium text-gray-900">
            {mode === 'create' ? 'Create Focus Area' : 'Manage Projects'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
          >
            <FaTimes />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {showCreateForm ? (
          <div className={`mb-4 bg-white border border-gray-200/60 rounded-xl p-3 ${mode === 'create' ? 'border-none p-0 bg-transparent mb-0' : ''}`}>
            {mode !== 'create' && <h3 className="text-base font-medium text-gray-900 mb-3">Create Focus Area</h3>}
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Focus Area Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Client Website, Personal Learning, Side Project"
                  className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this project..."
                  rows={2}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-200/60 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="px-2 py-1.5 border border-gray-200/60 rounded text-xs font-mono w-20"
                    placeholder="#f97316"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={!formData.name.trim() || !formData.color || isLoading}
                  className="px-4 py-2 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    if (mode === 'create') {
                      onClose();
                    } else {
                      setShowCreateForm(false);
                      setFormData({ name: '', description: '', color: '#f97316' });
                    }
                  }}
                  className="px-4 py-2 bg-white border border-gray-200/60 hover:border-gray-300/60 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Projects List - Only show in manage mode */}
        {mode === 'manage' && (
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Your Projects</h3>
            {isLoading && projects.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading projects...</div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No projects yet. Create your first project to start tracking!
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white border border-gray-200/60 rounded-xl p-4"
                >
                  {editingId === project.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Focus Area Name *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-gray-900 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Description</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={2}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200/60 rounded-lg text-gray-900 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Color</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="w-12 h-12 rounded-lg border border-gray-200/60 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="px-3 py-2 border border-gray-200/60 rounded-lg text-sm font-mono"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdate(project.id, formData)}
                          disabled={!formData.name.trim() || isLoading}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <FaSave className="text-xs" />
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 bg-white border border-gray-200/60 hover:border-gray-300/60 text-gray-700 rounded-lg font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0 mt-1"
                          style={{ backgroundColor: project.color || '#f97316' }}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-medium text-gray-900 truncate">{project.name}</h4>
                          {project.description && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => startEdit(project)}
                          className="p-2 text-gray-400 hover:text-orange-600 transition-colors"
                          title="Edit project"
                        >
                          <FaEdit className="text-sm" />
                        </button>
                        {!project.isDefault && <button
                          onClick={() => handleDeleteClick(project.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete project"
                        >
                          <FaTrash className="text-sm" />
                        </button>}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>


      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl border border-gray-100 transform scale-100 transition-all">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Project?</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Are you sure you want to delete this project? Upon delete, all sessions created using this project will be moved to the default project.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-lg shadow-red-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagementModal;



